import React, { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import AttendancePage from './components/AttendancePage';
import LoginPage from './components/LoginPage';
import AdminLogin from './components/AdminLogin';
import DesktopBlockWarning from './components/DesktopBlockWarning';
import CanhBaoPage from './components/CanhBaoPage';
import AppShell from './components/AppShell';
import { ToastProvider } from './components/ToastProvider';
import { isMobileDevice } from './utils/deviceDetection';
import { isBGDEmail } from './config/bgdEmails';
import { useDevToolsGuard } from './hooks/useDevToolsGuard';
import './App.css';
import './components/UIState.css';

const AdminPage = lazy(() => import('./components/AdminPage'));
const AttendancePageBGD = lazy(() => import('./components/AttendancePageBGD'));
const HistoryPage = lazy(() => import('./components/HistoryPage'));

const isDev = process.env.NODE_ENV === 'development';
const debugLog = (...args) => {
  if (isDev) {
    console.log(...args);
  }
};

function AppContent({ employee, setEmployee, isAdminLoggedIn, setIsAdminLoggedIn }) {
  const navigate = useNavigate();
  const [isDesktop, setIsDesktop] = useState(false);

  useEffect(() => {
    const isMobile = isMobileDevice();
    const isDesktopDevice = !isMobile;
    debugLog('[App] Device check - isMobile:', isMobile, 'isDesktop:', isDesktopDevice, 'employee:', employee?.name);
    setIsDesktop(isDesktopDevice);
  }, [employee]);

  const isVIP = (emp) => isBGDEmail(emp?.email);

  useEffect(() => {
    if (employee && isVIP(employee) && window.location.pathname === '/') {
      navigate('/vip', { replace: true });
    }
  }, [employee, navigate]);

  const handleEmployeeLogin = (emp) => {
    setEmployee(emp);
    localStorage.setItem('chamcong_employee', JSON.stringify(emp));
    if (isVIP(emp)) {
      navigate('/vip');
    }
  };

  const handleEmployeeLogout = useCallback(() => {
    setEmployee(null);
    localStorage.removeItem('chamcong_employee');
    navigate('/');
  }, [navigate, setEmployee]);

  const forceLogoutToWarning = useCallback(() => {
    setEmployee(null);
    localStorage.removeItem('chamcong_employee');
    navigate('/canh-bao', { replace: true });
  }, [navigate, setEmployee]);

  useDevToolsGuard({
    enabled: Boolean(employee && isDesktop),
    onDetected: forceLogoutToWarning,
  });

  const handleAdminLogin = () => {
    setIsAdminLoggedIn(true);
  };

  const handleNavigateToHistory = () => {
    debugLog('Navigating to /history using React Router');
    navigate('/history');
  };

  const shouldBlockEmployee = employee && isDesktop;

  useEffect(() => {
    if (employee) {
      debugLog('[App] Employee logged in:', employee.name);
      debugLog('[App] isDesktop:', isDesktop);
      debugLog('[App] shouldBlockEmployee:', shouldBlockEmployee);
    }
  }, [employee, isDesktop, shouldBlockEmployee]);

  const renderEmployeeShell = ({ content, onBackHome = null, showHistory = true }) => (
    <AppShell
      employee={employee}
      onHistory={showHistory ? handleNavigateToHistory : null}
      onBackHome={onBackHome}
      onLogout={handleEmployeeLogout}
    >
      {content}
    </AppShell>
  );

  return (
    <div className="app">
      <Suspense fallback={<div className="app-loading">Dang tai...</div>}>
        <Routes>
          <Route
            path="/"
            element={
              employee ? (
                shouldBlockEmployee ? (
                  renderEmployeeShell({
                    content: <DesktopBlockWarning onLogout={handleEmployeeLogout} showLogout={true} />,
                    showHistory: false,
                  })
                ) : (
                  renderEmployeeShell({
                    content: <AttendancePage employee={employee} apiBaseUrl={process.env.REACT_APP_API_URL || ''} />,
                  })
                )
              ) : (
                <LoginPage onLogin={handleEmployeeLogin} onAdminLogin={() => navigate('/admin-login')} />
              )
            }
          />

          <Route
            path="/admin-login"
            element={
              isAdminLoggedIn ? (
                <Navigate to="/admin" replace />
              ) : (
                <AdminLogin onLogin={handleAdminLogin} onBack={() => navigate('/')} />
              )
            }
          />

          <Route
            path="/admin"
            element={isAdminLoggedIn ? <AdminPage /> : <Navigate to="/admin-login" replace />}
          />

          <Route path="/canh-bao" element={<CanhBaoPage />} />

          <Route
            path="/vip"
            element={
              employee ? (
                isVIP(employee) ? (
                  renderEmployeeShell({
                    content: <AttendancePageBGD employee={employee} apiBaseUrl={process.env.REACT_APP_API_URL || ''} />,
                  })
                ) : (
                  <Navigate to="/" replace />
                )
              ) : (
                <Navigate to="/" replace />
              )
            }
          />

          <Route path="/bgd" element={<Navigate to="/vip" replace />} />

          <Route
            path="/history"
            element={
              employee ? (
                shouldBlockEmployee ? (
                  renderEmployeeShell({
                    content: <DesktopBlockWarning onLogout={handleEmployeeLogout} showLogout={true} />,
                    showHistory: false,
                  })
                ) : (
                  renderEmployeeShell({
                    content: <HistoryPage employee={employee} />,
                    onBackHome: () => navigate(isVIP(employee) ? '/vip' : '/'),
                    showHistory: false,
                  })
                )
              ) : (
                <Navigate to="/" replace />
              )
            }
          />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </div>
  );
}

function App() {
  const [employee, setEmployee] = useState(null);
  const [isAdminLoggedIn, setIsAdminLoggedIn] = useState(false);

  useEffect(() => {
    const savedEmployee = localStorage.getItem('chamcong_employee');
    if (savedEmployee) {
      try {
        setEmployee(JSON.parse(savedEmployee));
      } catch (e) {
        console.error('Error parsing saved employee:', e);
        localStorage.removeItem('chamcong_employee');
      }
    }
  }, []);

  return (
    <Router>
      <ToastProvider>
        <AppContent
          employee={employee}
          setEmployee={setEmployee}
          isAdminLoggedIn={isAdminLoggedIn}
          setIsAdminLoggedIn={setIsAdminLoggedIn}
        />
      </ToastProvider>
    </Router>
  );
}

export default App;
